import { useState, useEffect } from 'react';
import { X, Mail, Phone, User, MapPin, MessageSquare, Send, ArrowRight, Instagram, Bell, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ActivityTimeline from './ActivityTimeline';
import EmailDrafter from './EmailDrafter';
import type { Prospect } from './ProspectCard';
import type { Activity } from './ActivityTimeline';

const STATUSES = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'in_conversation', label: 'In Conversation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const TEAM_MEMBERS = ['Zain', 'Stiofan'];

interface ProspectDetailPanelProps {
  prospect: Prospect;
  onClose: () => void;
  onUpdate: () => void;
}

const getDaysSince = (date: string | null): number | null => {
  if (!date) return null;
  const diff = Date.now() - new Date(date).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const getFollowUpRecommendation = (prospect: Prospect) => {
  const days = getDaysSince(prospect.last_contacted_at);
  if (days === null || days < 3) return null;
  if (prospect.status === 'won' || prospect.status === 'lost') return null;

  if (days >= 5 && prospect.contact_phone) return { method: 'Phone', detail: prospect.contact_phone, icon: Phone };
  if (days >= 4 && prospect.instagram) return { method: 'Instagram', detail: prospect.instagram, icon: Instagram };
  if (prospect.contact_email) return { method: 'Email', detail: prospect.contact_email, icon: Mail };
  if (prospect.instagram) return { method: 'Instagram', detail: prospect.instagram, icon: Instagram };
  if (prospect.contact_phone) return { method: 'Phone', detail: prospect.contact_phone, icon: Phone };
  return { method: 'Email', detail: prospect.contact_email || 'No email on file', icon: Mail };
};

const ProspectDetailPanel = ({ prospect, onClose, onUpdate }: ProspectDetailPanelProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [note, setNote] = useState('');
  const [emailDrafter, setEmailDrafter] = useState<'outreach' | 'follow_up' | null>(null);
  const [status, setStatus] = useState(prospect.status);
  const [assignedTo, setAssignedTo] = useState(prospect.assigned_to || '');

  const followUp = getFollowUpRecommendation(prospect);

  const fetchActivities = async () => {
    const { data } = await supabase
      .from('prospect_activities')
      .select('*')
      .eq('prospect_id', prospect.id)
      .order('created_at', { ascending: false });
    if (data) setActivities(data);
  };

  useEffect(() => { fetchActivities(); }, [prospect.id]);

  const handleStatusChange = async (newStatus: string) => {
    setStatus(newStatus);
    const now = ['contacted', 'in_conversation', 'follow_up'].includes(newStatus) ? new Date().toISOString() : undefined;
    await supabase.from('prospects').update({
      status: newStatus as any,
      ...(now ? { last_contacted_at: now } : {}),
    }).eq('id', prospect.id);

    await supabase.from('prospect_activities').insert({
      prospect_id: prospect.id,
      type: 'status_change',
      content: `Status changed to ${STATUSES.find(s => s.value === newStatus)?.label}`,
    });
    fetchActivities();
    onUpdate();
  };

  const handleAssign = async (member: string) => {
    setAssignedTo(member);
    await supabase.from('prospects').update({ assigned_to: member || null }).eq('id', prospect.id);
    onUpdate();
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    await supabase.from('prospect_activities').insert({
      prospect_id: prospect.id,
      type: 'note',
      content: note.trim(),
    });
    setNote('');
    fetchActivities();
    toast.success('Note added');
  };

  const handleFollowUpClick = (rec: { method: string; detail: string }) => {
    if (rec.method === 'Email' && prospect.contact_email) {
      window.open(`mailto:${prospect.contact_email}`, '_blank');
    } else if (rec.method === 'Instagram' && prospect.instagram) {
      const handle = prospect.instagram.replace('@', '').replace('https://instagram.com/', '').replace('https://www.instagram.com/', '');
      window.open(`https://instagram.com/${handle}`, '_blank');
    } else if (rec.method === 'Phone' && prospect.contact_phone) {
      window.open(`tel:${prospect.contact_phone}`, '_blank');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-foreground text-lg">{prospect.name}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Follow-up Recommendation */}
          {followUp && (
            <button
              onClick={() => handleFollowUpClick(followUp)}
              className="w-full flex items-center gap-3 bg-amber-50 border-2 border-amber-300 rounded-xl p-3 text-left hover:bg-amber-100 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                <Bell className="w-4.5 h-4.5 text-amber-700 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-800">Follow up recommended</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {getDaysSince(prospect.last_contacted_at)}d since last contact - try {followUp.method.toLowerCase()}
                </p>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-200/80 px-2.5 py-1.5 rounded-lg group-hover:bg-amber-300 transition-colors">
                <followUp.icon className="w-4 h-4 text-amber-800" />
                <span className="text-xs font-semibold text-amber-800">{followUp.method}</span>
              </div>
            </button>
          )}

          {/* Contact info */}
          <div className="space-y-2">
            {prospect.address && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                {prospect.address}
              </div>
            )}
            {prospect.contact_name && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <User className="w-4 h-4 text-muted-foreground shrink-0" />
                {prospect.contact_name}{prospect.contact_role && ` · ${prospect.contact_role}`}
              </div>
            )}
            {prospect.contact_email && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <a href={`mailto:${prospect.contact_email}`} className="underline">{prospect.contact_email}</a>
              </div>
            )}
            {prospect.contact_phone && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                {prospect.contact_phone}
              </div>
            )}
            {prospect.website && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                <a href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`} target="_blank" rel="noopener noreferrer" className="underline truncate">{prospect.website}</a>
              </div>
            )}
            {prospect.instagram && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Instagram className="w-4 h-4 text-muted-foreground shrink-0" />
                <a
                  href={`https://instagram.com/${prospect.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {prospect.instagram.startsWith('@') ? prospect.instagram : `@${prospect.instagram}`}
                </a>
              </div>
            )}
          </div>

          {/* Status & Assignment */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
              <select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Assigned To</label>
              <select
                value={assignedTo}
                onChange={(e) => handleAssign(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                <option value="">Unassigned</option>
                {TEAM_MEMBERS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setEmailDrafter('outreach')} className="flex-1">
              <Send className="w-3.5 h-3.5 mr-1" /> Draft Email
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEmailDrafter('follow_up')} className="flex-1">
              <ArrowRight className="w-3.5 h-3.5 mr-1" /> Draft Follow-up
            </Button>
          </div>

          {/* Add Note */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Add a note</label>
            <div className="flex gap-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                placeholder="Log a call, meeting, etc."
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
              <Button size="sm" variant="outline" onClick={handleAddNote} disabled={!note.trim()}>
                <MessageSquare className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Activity</h4>
            <ActivityTimeline activities={activities} />
          </div>
        </div>
      </div>

      {emailDrafter && (
        <EmailDrafter
          prospect={prospect}
          templateType={emailDrafter}
          onClose={() => setEmailDrafter(null)}
          onDrafted={() => { fetchActivities(); setEmailDrafter(null); onUpdate(); }}
        />
      )}
    </>
  );
};

export default ProspectDetailPanel;
